/**
 * SOW template admin API (studio data layer)
 * ──────────────────────────────────────────
 * Typed wrappers over the `/workspace/admin/products/:uuid/sow-templates`
 * endpoints, shared by the SOW Template Studio. Unlike `apiFetch` (which
 * surfaces only an error message), these read the full 409 body so the studio
 * can act on the structured `PUBLISH_BLOCKED` conformance + `STALE_WRITE`
 * current-version payloads. Auth + transient-retry come from `apiClient`.
 */

import type { DocumentContract } from '@protoss/doc';
import { fetchWithRetry, getAuthToken } from '../../../lib/apiClient';
import type { PreviewSample } from '../../../components/doc';
import type { SowFidelityMetadata } from '../components/sow-template/SowFidelityPanel';

export interface SowStructuralConformance {
  score?: number;
  issues?: string[];
}

/** A SOW template as returned by the admin list endpoint (full contract + metadata). */
export interface SowStudioTemplate {
  id: number;
  uuid: string;
  label: string;
  version: string;
  isDefault: boolean;
  status: string;
  productKey: string | null;
  exportType?: string;
  provider?: string;
  scope: 'system' | 'workspace' | 'user';
  categoryKey: string | null;
  contract: unknown;
  metadata?: {
    previewSample?: PreviewSample;
    fidelity?: SowFidelityMetadata;
    assetSlots?: Record<string, string>;
    structuralConformance?: SowStructuralConformance;
  } | null;
}

/** Authenticated JSON request that returns the status + parsed body for any status. */
async function authedJson<T>(path: string, init?: RequestInit): Promise<{ status: number; body: T }> {
  const token = await getAuthToken();
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetchWithRetry(path, { ...init, headers });
  const body = (await res.json().catch(() => ({}))) as T;
  return { status: res.status, body };
}

function base(productUuid: string): string {
  return `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/sow-templates`;
}

export async function fetchSowTemplates(productUuid: string): Promise<SowStudioTemplate[]> {
  const { status, body } = await authedJson<{ templates?: SowStudioTemplate[]; message?: string }>(
    base(productUuid)
  );
  if (status !== 200) throw new Error(body.message ?? `Failed to load templates (${status})`);
  return body.templates ?? [];
}

export interface SaveSowTemplateInput {
  contract: DocumentContract;
  previewSample: PreviewSample;
  assetSlots?: Record<string, string>;
  /** The version the client loaded; omit to force-overwrite a stale write. */
  expectedVersion?: string;
}

export type SaveSowTemplateResult =
  | { ok: true; version: string }
  | { ok: false; code: 'STALE_WRITE'; currentVersion?: string; message: string }
  | {
      ok: false;
      code: 'INVALID_CONTRACT';
      issues?: Array<{ path: string; message: string }>;
      message: string;
    }
  | { ok: false; code: 'CANNOT_EDIT_SYSTEM' | 'ERROR'; message: string };

export async function saveSowTemplate(
  productUuid: string,
  templateUuid: string,
  input: SaveSowTemplateInput
): Promise<SaveSowTemplateResult> {
  const { status, body } = await authedJson<{
    template?: { version: string };
    error?: string;
    message?: string;
    issues?: Array<{ path: string; message: string }>;
    current?: { version: string };
  }>(`${base(productUuid)}/${encodeURIComponent(templateUuid)}`, {
    method: 'PATCH',
    body: JSON.stringify({
      contract: input.contract,
      previewSample: input.previewSample,
      ...(input.assetSlots ? { assetSlots: input.assetSlots } : {}),
      ...(input.expectedVersion ? { expectedVersion: input.expectedVersion } : {}),
    }),
  });
  if (status === 200 && body.template) return { ok: true, version: body.template.version };
  const message = body.message ?? `Save failed (${status})`;
  if (status === 409 && body.error === 'STALE_WRITE') {
    return { ok: false, code: 'STALE_WRITE', currentVersion: body.current?.version, message };
  }
  if (status === 422 || body.error === 'INVALID_CONTRACT') {
    return { ok: false, code: 'INVALID_CONTRACT', issues: body.issues, message };
  }
  if (status === 409 && body.error === 'CANNOT_EDIT_SYSTEM_TEMPLATE') {
    return { ok: false, code: 'CANNOT_EDIT_SYSTEM', message };
  }
  return { ok: false, code: 'ERROR', message };
}

export type PublishSowTemplateResult =
  | { ok: true }
  | {
      ok: false;
      code: 'PUBLISH_BLOCKED';
      conformance?: { score: number; threshold: number };
      message: string;
    }
  | { ok: false; code: 'CANNOT_EDIT_SYSTEM' | 'ERROR'; message: string };

export async function publishSowTemplate(
  productUuid: string,
  templateUuid: string,
  opts?: { force?: boolean }
): Promise<PublishSowTemplateResult> {
  const qs = opts?.force ? '?force=true' : '';
  const { status, body } = await authedJson<{
    error?: string;
    message?: string;
    conformance?: { score: number; threshold: number };
  }>(`${base(productUuid)}/${encodeURIComponent(templateUuid)}/publish${qs}`, { method: 'POST' });
  if (status === 200) return { ok: true };
  const message = body.message ?? `Publish failed (${status})`;
  if (status === 409 && body.error === 'PUBLISH_BLOCKED') {
    return { ok: false, code: 'PUBLISH_BLOCKED', conformance: body.conformance, message };
  }
  if (status === 409 && body.error === 'CANNOT_EDIT_SYSTEM_TEMPLATE') {
    return { ok: false, code: 'CANNOT_EDIT_SYSTEM', message };
  }
  return { ok: false, code: 'ERROR', message };
}

export interface CatalogToken {
  token: string;
  description: string;
  source: 'context' | 'data' | 'ai_section';
}

export async function fetchTokenCatalog(
  productUuid: string,
  categoryKey?: string | null
): Promise<CatalogToken[]> {
  const qs = categoryKey ? `?categoryKey=${encodeURIComponent(categoryKey)}` : '';
  const { status, body } = await authedJson<{ tokens?: CatalogToken[]; message?: string }>(
    `/api/workspace/admin/products/${encodeURIComponent(productUuid)}/sow/token-catalog${qs}`
  );
  if (status !== 200) throw new Error(body.message ?? `Failed to load tokens (${status})`);
  return body.tokens ?? [];
}

/** Promote a detected color to the workspace brand (the global `primary_color`). */
export async function setWorkspaceBrandColor(hex: string): Promise<void> {
  const value = hex.replace(/^#/, '').toUpperCase();
  const { status, body } = await authedJson<{
    error?: { message?: string };
    message?: string;
  }>('/api/workspace/admin/brand/colors/primary_color', {
    method: 'PUT',
    body: JSON.stringify({ value }),
  });
  if (status !== 200) {
    throw new Error(body.error?.message ?? body.message ?? `Failed to set brand color (${status})`);
  }
}

export async function cloneSowTemplate(
  productUuid: string,
  sourceTemplateUuid: string,
  label: string
): Promise<{ uuid: string }> {
  const { status, body } = await authedJson<{ template?: { uuid: string }; message?: string }>(
    base(productUuid),
    { method: 'POST', body: JSON.stringify({ sourceTemplateUuid, label }) }
  );
  if (status === 201 && body.template) return { uuid: body.template.uuid };
  throw new Error(body.message ?? `Duplicate failed (${status})`);
}
